import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  MessageModal,
  MultipleDropdown,
  RadioGroup,
  TextInput,
  Tooltip,
  toggleObject,
} from '@makeably/creativex-design-system';
import { taxonomyProps } from 'components/creative_campaigns/Taxonomies';
import { addToast } from 'components/organisms/Toasts';
import { optionArrayProps } from 'components/shared';
import {
  addFormDataArray,
  addFormDataObject,
  patch,
  post,
} from 'utilities/requests';
import {
  taxonomyCreativeTaxonomiesPath,
  taxonomyCreativeTaxonomyPath,
} from 'utilities/routes';
import './TaxonomyModal.module.css';

const propTypes = {
  channelOptions: optionArrayProps.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partnerOptions: optionArrayProps.isRequired,
  taxonomy: taxonomyProps,
};

const defaultProps = {
  taxonomy: undefined,
};

const KEY_OPTION = 'key';
const POSITION_OPTION = 'position';
const KEY_TOOLTIP = 'Values and their codes are deciphered by a key, regardless of position in the string';
const POSITION_TOOLTIP = 'Values and their codes are deciphered by their position in a string';
const EXAMPLE_KEY_TAXONOMY = 'Example: ID~XXXX_MK~XXXX_AR~XXXX...';
const EXAMPLE_POSITION_TAXONOMY = 'Example: Region_Brand_Campaign Type_Campaign Name...';

const radioOptions = [
  {
    label: (
      <div className="u-flexRow u-gap-8 u-alignCenter">
        Key Based
        <Tooltip label={KEY_TOOLTIP} />
        <div className="t-caption-1 u-marginLeft-8">
          { EXAMPLE_KEY_TAXONOMY }
        </div>
      </div>
    ),
    value: KEY_OPTION,
  },
  {
    label: (
      <div className="u-flexRow u-gap-8 u-alignCenter">
        Position Based
        <Tooltip label={POSITION_TOOLTIP} />
        <div className="t-caption-1 u-marginLeft-8">
          { EXAMPLE_POSITION_TAXONOMY }
        </div>
      </div>
    ),
    value: POSITION_OPTION,
  },
];

async function submitTaxonomy(url, isUpdate, taxonomy) {
  const formData = new FormData();

  const data = {
    delimiter: taxonomy.delimiter,
    name: taxonomy.name,
  };
  if (taxonomy.type === KEY_OPTION) {
    data.key = taxonomy.indicator;
  } else {
    data.position = taxonomy.indicator;
  }
  addFormDataObject(formData, 'taxonomy', data);
  addFormDataArray(formData, 'channels', taxonomy.channels);
  addFormDataArray(formData, 'partners', taxonomy.partners);

  let response;
  if (isUpdate) {
    response = await patch(url, formData);
  } else {
    response = await post(url, formData);
  }

  return response.data;
}

async function updateTaxonomy({ id, ...rest }) {
  return submitTaxonomy(taxonomyCreativeTaxonomyPath(id), true, rest);
}

async function createTaxonomy(data) {
  return submitTaxonomy(taxonomyCreativeTaxonomiesPath(), false, data);
}

function findCampaignByKey(fields, key) {
  const field = fields.find((f) => f.startsWith(key));
  return field?.slice(key.length);
}

function findCampaign(keyBased, fields, indicator) {
  if (keyBased) return findCampaignByKey(fields, indicator);

  // Position for indicator is 1-based, not 0-based
  return fields[indicator - 1];
}

function TaxonomyModal({
  channelOptions,
  isOpen,
  onClose,
  partnerOptions,
  taxonomy,
}) {
  const initialChannels = channelOptions.filter((opt) => taxonomy?.channels.includes(opt.value));
  const initialPartners = partnerOptions.filter((opt) => taxonomy?.partners.includes(opt.value));

  const [name, setName] = useState(taxonomy?.name);
  const [type, setType] = useState(taxonomy?.taxonomyType ?? KEY_OPTION);
  const keyBased = type === KEY_OPTION;
  const [channelSelections, setChannelSelections] = useState(initialChannels);
  const [partnerSelections, setPartnerSelections] = useState(initialPartners);
  const [indicator, setIndicator] = useState(keyBased ? taxonomy?.key : taxonomy?.position);
  const [delimiter, setDelimiter] = useState(taxonomy?.delimiter);
  const [sampleTaxonomy, setSampleTaxonomy] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedCampaign = useMemo(() => {
    if (sampleTaxonomy) {
      const fields = sampleTaxonomy.split(delimiter);
      return findCampaign(keyBased, fields, indicator);
    }
    return null;
  }, [sampleTaxonomy, type, indicator, delimiter]);

  const parsedMessage = parsedCampaign ? `Parsed Campaign: ${parsedCampaign}` : 'No Campaign Parsed';
  const incompleteForm = channelSelections.length === 0
    || partnerSelections.length === 0
    || !name
    || !indicator
    || !delimiter;

  const toggleType = () => {
    setType(keyBased ? POSITION_OPTION : KEY_OPTION);
    setIndicator('');
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    const data = {
      channels: channelSelections.map((selection) => selection.value),
      delimiter,
      id: taxonomy?.id,
      indicator,
      name,
      partners: partnerSelections.map((selection) => selection.value),
      type,
    };

    let response;
    if (data.id) {
      response = await updateTaxonomy(data);
    } else {
      response = await createTaxonomy(data);
    }

    if (response) {
      if (response.message) {
        addToast(response.message, { type: response.success ? 'success' : 'error' });
      } else {
        addToast('Taxonomy Structure Successfully Saved');
      }
      window.location.reload();
    } else {
      addToast('An error occurred while saving the taxonomy', { type: 'error' });
      setIsSubmitting(false);
    }
  };

  const nameLabel = (
    <div className="t-caption-1 u-flexRow u-gap-8 u-alignCenter">
      Taxonomy Rule Name
      <Tooltip label="This is for your reference and will not be populated on any other screens" />
    </div>
  );

  return (
    <MessageModal
      actionButtonActive={isSubmitting}
      actionButtonDisabled={incompleteForm}
      actionButtonLabel="Save"
      isOpen={isOpen}
      size="medium"
      title={`${taxonomy ? 'Edit' : 'Add'} Campaign Name Taxonomy`}
      onActionButtonClick={handleSave}
      onClose={onClose}
    >
      <div className="u-flexColumn u-gap-16">
        <TextInput
          label={nameLabel}
          name="name"
          placeholder="Add Name"
          size="large"
          value={name}
          onChange={(key) => setName(key)}
        />
        <RadioGroup
          name="typeSelectionRadioGroup"
          options={radioOptions}
          value={type}
          onChange={() => toggleType()}
        />
        <div className="u-flexRow u-gap-16">
          <TextInput
            label={keyBased ? 'Key' : 'Position'}
            name="indicator"
            placeholder={keyBased ? 'Add Key' : 'Select'}
            type={keyBased ? 'text' : 'number'}
            value={indicator}
            onChange={(key) => setIndicator(key)}
          />
          <TextInput
            label="Delimiter"
            name="delimiter"
            placeholder="Add Delimiter"
            type="text"
            value={delimiter}
            onChange={(key) => setDelimiter(key)}
          />
        </div>
        <MultipleDropdown
          label="Applicable Channels"
          options={channelOptions}
          selected={channelSelections}
          onChange={(opt) => setChannelSelections(toggleObject(channelSelections, opt))}
          onSelectAll={setChannelSelections}
        />
        <MultipleDropdown
          label="Applicable Partners"
          options={partnerOptions}
          selected={partnerSelections}
          onChange={(opt) => setPartnerSelections(toggleObject(partnerSelections, opt))}
          onSelectAll={setPartnerSelections}
        />
        <div className="u-flexColumn u-gap-8">
          <TextInput
            label="Paste Sample Taxonomy"
            name="example_taxonomy"
            placeholder="Optional"
            size="large"
            value={sampleTaxonomy}
            onChange={(key) => setSampleTaxonomy(key)}
          />
          <div className="t-empty">
            { parsedMessage }
          </div>
        </div>
      </div>
    </MessageModal>
  );
}

TaxonomyModal.propTypes = propTypes;
TaxonomyModal.defaultProps = defaultProps;

export default TaxonomyModal;
